import type { DOMAttributes, ReactElement, ReactNode } from 'react';
import { PreviewTrigger } from 'react-aria-components/PreviewTrigger';
import { Dialog, DialogTrigger, Focusable, OverlayArrow, Popover } from '@launchpad-ui/components';

import { ContextPopover } from '../../internal/components/ContextPopover/ContextPopover';

import styles from './ContextDetailsPopover.module.css';

interface ContextDetailsPopoverBaseProps {
  /** Kind of the context being previewed (e.g. `user`). */
  readonly contextKind: string;
  /** Key of the context being previewed. */
  readonly contextKey: string;
  /** Project key used to resolve the context and build the external link. */
  readonly projectKey: string;
  /** Environment key used to resolve the context and build the external link. */
  readonly environmentKey: string;
}

type ContextDetailsPopoverProps = ContextDetailsPopoverBaseProps &
  (
    | {
        readonly trigger?: 'press';
        readonly children: ReactNode;
      }
    | {
        readonly trigger: 'hover';
        readonly children: ReactElement<DOMAttributes<Element>, string>;
      }
  );

export function ContextDetailsPopover(props: ContextDetailsPopoverProps) {
  const { contextKind, contextKey, projectKey, environmentKey } = props;
  const content = (
    <>
      <OverlayArrow />
      <ContextPopover
        contextKind={contextKind}
        contextKey={contextKey}
        projectKey={projectKey}
        environmentKey={environmentKey}
        isPreview={props.trigger === 'hover'}
      />
    </>
  );

  if (props.trigger === 'hover') {
    return (
      <PreviewTrigger delay={300}>
        <Focusable>{props.children}</Focusable>
        <Popover className={styles.hoverPopover}>{content}</Popover>
      </PreviewTrigger>
    );
  }

  return (
    <DialogTrigger>
      {props.children}
      <Popover>
        <Dialog>{content}</Dialog>
      </Popover>
    </DialogTrigger>
  );
}
